import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { 
  Building2, 
  Truck, 
  ArrowLeft, 
  ArrowRight,
  Eye,
  EyeOff,
  Mail,
  Phone,
  Lock,
  User,
  MapPin,
  FileText,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { authService } from '../services';
import HospitalRegistrationForm from '../components/registration/HospitalRegistrationForm';
import FleetOperatorRegistrationForm from '../components/registration/FleetOperatorRegistrationForm';

const SuperAdminRegistrationPage = ({ dashboardType = 'admin' }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth(); 
  const [activeTab, setActiveTab] = useState('hospital');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Check authentication on component mount
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate('/login', { 
          state: { 
            message: 'Please login as Super Admin to register organizations' 
          } 
        });
        return;
      }
      
      if (user?.role !== 'SUPER_ADMIN' && user?.role !== 'SUPER_ADMIN_USER') {
        navigate('/admin', { 
          state: { 
            message: 'Only Super Admins can register organizations' 
          } 
        });
        return;
      }
    }
  }, [isAuthenticated, user, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto"></div>
          <p className="text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const handleSuccess = (result) => {
    // Create a more detailed success message
    const organizationType = activeTab === 'hospital' ? 'Hospital' : 'Fleet Operator';
    const organizationName = result.data?.hospital?.name || result.data?.operator?.name || 'Organization';
    
    const successMsg = `${organizationType} "${organizationName}" has been registered successfully! The admin user can now login with their credentials.`;
    
    setSuccessMessage(successMsg);
    setErrorMessage('');
    
    // Clear success message after 8 seconds (longer for better readability)
    setTimeout(() => {
      setSuccessMessage('');
    }, 8000);
  };

  const handleError = (error) => {
    setErrorMessage(error);
    setSuccessMessage('');
    
    // Clear error message after 5 seconds
    setTimeout(() => {
      setErrorMessage('');
    }, 5000);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Features & Branding */}
          <div className="hidden lg:block space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#1e3a8a] rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Super Admin Portal</h1>
              </div>
              <p className="text-lg font-semibold text-gray-600 max-w-md">
                Register hospitals and fleet operators to join the Smart Ambulance platform.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Hospital Registration</h3>
                  <p className="text-sm text-gray-600">Register hospitals with admin user accounts for seamless integration</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Fleet Operator Registration</h3>
                  <p className="text-sm text-gray-600">Register ambulance fleet operators with admin user accounts</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Instant Access</h3>
                  <p className="text-sm text-gray-600">Registered users can immediately login and access their dashboards</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Registration Forms */}
          <div className="w-full max-w-md mx-auto lg:max-w-none">
            <Card className="shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-2 text-center pb-8">
                <div className="flex items-center justify-center space-x-3 mb-4 lg:hidden">
                  <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">Super Admin Portal</h1>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Register New Organization</CardTitle>
                <CardDescription className="text-gray-600">
                  Choose the type of organization to register
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Success/Error Messages */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800 mb-1">Registration Successful!</p>
                        <p className="text-sm text-green-700 mb-3">{successMessage}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSuccessMessage('');
                            setErrorMessage('');
                            // Reset form by changing tab and back
                            const currentTab = activeTab;
                            setActiveTab(currentTab === 'hospital' ? 'fleet' : 'hospital');
                            setTimeout(() => setActiveTab(currentTab), 100);
                          }}
                          className="text-green-700 border-green-300 hover:bg-green-100"
                        >
                          Register Another Organization
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800 mb-1">Registration Failed</p>
                        <p className="text-sm text-red-700">{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Registration Tabs */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="hospital" className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4" />
                      <span className="hidden sm:inline">Hospital</span>
                    </TabsTrigger>
                    <TabsTrigger value="fleet" className="flex items-center space-x-2">
                      <Truck className="w-4 h-4" />
                      <span className="hidden sm:inline">Fleet Operator</span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="hospital" className="mt-6">
                    <HospitalRegistrationForm 
                      onSuccess={handleSuccess}
                      onError={handleError}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                    />
                  </TabsContent>

                  <TabsContent value="fleet" className="mt-6">
                    <FleetOperatorRegistrationForm 
                      onSuccess={handleSuccess}
                      onError={handleError}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                    />
                  </TabsContent>
                </Tabs>

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="flex items-center space-x-2"
                    disabled={isLoading}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin')}
                    className="flex items-center space-x-2"
                    disabled={isLoading}
                  >
                    <span>Go to Dashboard</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Help Text */}
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-600">
                    Need help? Contact{' '}
                    <a 
                      href="mailto:support@smartambulance.com" 
                      className="text-[#1e3a8a] hover:text-blue-700 font-medium transition-colors"
                    >
                      support@smartambulance.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminRegistrationPage;
